import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { notifyUser } from '../lib/notify';
import './BidFlow.css';

function formatPriceFull(p) {
  if (!p) return null;
  return '$' + p.toLocaleString();
}

export default function BidFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [notifMethod, setNotifMethod] = useState('email');
  const [closeTimeline, setCloseTimeline] = useState('30');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!id) return;
    supabase
      .from('properties')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error }) => {
        if (data) {
          setProperty(data);
          const defaultBid = data.asking_price
            ? data.asking_price + 7000
            : data.zestimate;
          setBidAmount(defaultBid.toLocaleString());
        }
        setLoading(false);
      });
  }, [id]);

  async function handleSubmit() {
    if (!user) {
      setError('Please sign in to place a bid.');
      return;
    }
    setSubmitting(true);
    setError('');

    const amount = parseInt(bidAmount.replace(/,/g, ''));
    if (!amount || amount < 1000) {
      setError('Please enter a valid bid amount.');
      setSubmitting(false);
      return;
    }

    const { error } = await supabase
      .from('bids')
      .insert({
        property_id: property.id,
        buyer_id: user.id,
        amount,
        closing_timeline: closeTimeline,
        message,
        status: 'pending'
      });

    if (error) {
      setError(error.message);
      setSubmitting(false);
      return;
    }

    // Notify owner if property has one
    if (property.owner_id) {
      await notifyUser({
        userId: property.owner_id,
        type: 'bid_received',
        title: 'New bid received',
        body: `A buyer placed a bid of $${amount.toLocaleString()} on ${property.address}`,
        relatedPropertyId: property.id,
      });
    }

    // Update user notification preference
    await supabase
      .from('profiles')
      .update({ notification_method: notifMethod })
      .eq('id', user.id);

    setSubmitted(true);
    setSubmitting(false);
  }

  if (loading) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ color: 'var(--text-secondary)' }}>loading property...</div>
    </div>
  );

  if (!property) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ color: 'var(--text-secondary)' }}>property not found.</div>
    </div>
  );

  const isOffMarket = property.status === 'off-market';
  const bidNum = parseInt(bidAmount.replace(/,/g, '')) || 0;
  const diff = bidNum - property.zestimate;
  const diffLabel = diff > 0
    ? `↑ $${Math.abs(diff).toLocaleString()} above Zestimate — competitive range`
    : diff < 0
    ? `↓ $${Math.abs(diff).toLocaleString()} below Zestimate`
    : 'At Zestimate';
  const diffColor = diff > 0 ? '#3B6D11' : diff < 0 ? '#A32D2D' : '#555';
  const rangePos = Math.min(100, Math.max(0,
    ((property.zestimate - property.zest_low) / (property.zest_high - property.zest_low)) * 100
  ));

  if (submitted) {
    return (
      <div className="page">
        <div className="bid-confirm card">
          <div className="confirm-icon">✓</div>
          <h2>Bid submitted!</h2>
          <p>Your bid of <strong>{formatPriceFull(parseInt(bidAmount.replace(/,/g, '')))}</strong> on {property.address} has been recorded.</p>
          <p style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
            You'll be notified via {notifMethod.toUpperCase()} when the owner responds.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => navigate('/letter-service')}>
              ✉ send owner a letter — stand out
            </button>
            <button className="btn" onClick={() => navigate('/buyer-dashboard')}>go to my bids</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="two-col">
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            {/* Property header */}
            <div className="bid-prop-header">
              <div className="bid-prop-thumb" style={{ background: '#dde8f2' }}>
                <svg viewBox="0 0 60 45" width="60">
                  <polygon points="30,3 56,22 48,22 48,43 12,43 12,22 4,22" fill="#185FA5" opacity="0.4"/>
                  <rect x="22" y="28" width="16" height="15" fill="#185FA5" opacity="0.6"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {isOffMarket ? `est. ${formatPriceFull(property.zestimate)}` : formatPriceFull(property.asking_price)}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {property.address}, {property.city}, {property.state} {property.zip}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  <span>{property.beds} bd</span>
                  <span>{property.baths} ba</span>
                  <span>{property.sqft?.toLocaleString()} sqft</span>
                </div>
              </div>
            </div>

            {/* Zestimate bar */}
            <div className="zest-bar">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Zillow Zestimate®</span>
                <span style={{ fontSize: 18, fontWeight: 600 }}>{formatPriceFull(property.zestimate)}</span>
              </div>
              <div className="range-track">
                <div className="range-fill" />
                <div className="range-marker" style={{ left: `${rangePos}%` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                <span>{formatPriceFull(property.zest_low)}</span>
                <span>likely range</span>
                <span>{formatPriceFull(property.zest_high)}</span>
              </div>
            </div>

            {/* Insights */}
            <div className="insight-row">
              <div className="insight">
                <div className="val">{property.bids?.[0]?.count ?? 0}</div>
                <div className="lbl">active bids</div>
              </div>
              <div className="insight"><div className="val">blind</div><div className="lbl">bid visibility</div></div>
              <div className="insight">
                <div className="val">{property.days_on_market ?? '—'}</div>
                <div className="lbl">{property.days_on_market ? 'days on market' : 'off-market'}</div>
              </div>
            </div>

            <div className="divider" />

            {/* Bid input */}
            <h3 style={{ marginBottom: 12 }}>your bid</h3>

            {!user && (
              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400E' }}>
                Please sign in to place a bid.
              </div>
            )}

            <div className="input-group">
              <label>bid amount</label>
              <div className="dollar-input">
                <span>$</span>
                <input
                  value={bidAmount}
                  onChange={e => {
                    const raw = e.target.value.replace(/,/g, '');
                    if (!isNaN(raw)) setBidAmount(parseInt(raw || 0).toLocaleString());
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: diffColor, marginTop: 5 }}>{diffLabel}</div>
            </div>

            <div className="input-group">
              <label>closing timeline preference</label>
              <select value={closeTimeline} onChange={e => setCloseTimeline(e.target.value)}>
                <option value="30">30 days</option>
                <option value="45">45 days</option>
                <option value="60">60 days</option>
                <option value="flex">flexible</option>
              </select>
            </div>

            <div className="input-group">
              <label>message to owner (optional)</label>
              <textarea
                rows={3}
                placeholder="Introduce yourself or share why you love this home..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="divider" />

            <h3 style={{ marginBottom: 10 }}>notify me via</h3>
            <div className="notif-row">
              {[['in-app', 'in-app'], ['email', 'email'], ['sms', 'SMS'], ['all', 'all']].map(([val, label]) => (
                <div
                  key={val}
                  className={`notif-opt ${notifMethod === val ? 'selected' : ''}`}
                  onClick={() => setNotifMethod(val)}
                >
                  <input type="radio" readOnly checked={notifMethod === val} style={{ accentColor: 'var(--blue)' }} />
                  <span>{label}</span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginTop: 12 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                className="btn btn-primary btn-full"
                onClick={handleSubmit}
                disabled={submitting || !user}
              >
                {submitting ? 'submitting...' : `submit bid — ${formatPriceFull(parseInt(bidAmount.replace(/,/g, '')) || 0)}`}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>how blind bidding works</h3>
            {[
              'You submit a bid — other buyers cannot see your amount, only that bids exist.',
              'The owner reviews all bids privately and can accept, decline, or counter any of them.',
              "You're notified instantly via your preferred method when your bid status changes."
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                <div className="step-num">{i + 1}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{t}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 10 }}>nearby sales (90 days)</h3>
            {[
              ['1388 Magnolia Dr', '$476,000'],
              ['214 Elmwood Ave', '$498,500'],
              ['57 Oak Terrace', '$469,000'],
            ].map(([a, p]) => (
              <div key={a} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{a}</span>
                <span style={{ fontWeight: 500 }}>{p}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>avg/sqft</span>
              <span style={{ fontWeight: 500 }}>$261/sqft</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
